import DOMPurify from "dompurify";

const EL_COLORS = { fire: "#ef4444", water: "#06b6d4", wind: "#10b981", earth: "#d97706" };
const EL_DESC = { fire: "Drive & Direction", water: "Care & Connection", wind: "Clarity & Adaptability", earth: "Stability & Structure" };

const esc = (str) => String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

const buildLegendHtml = (sorted, profile) => sorted.map(([el]) => {
  const isTop = el === profile.dominant || el === profile.secondary;
  const roleLabel = el === profile.dominant
    ? '<span style="color:#FF751F;font-size:11px;font-weight:600;">&#9632; Dominant Element</span>'
    : el === profile.secondary
      ? '<span style="color:#FFBD25;font-size:11px;font-weight:600;">&#9632; Secondary Element</span>'
      : '';
  return `<div style="display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;border:2px solid ${isTop ? '#FF751F' : '#e5e7eb'};background:${isTop ? '#fff7ed' : '#fff'};margin-bottom:8px;">
    <div style="width:32px;height:32px;border-radius:8px;background:${EL_COLORS[el]};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
      <span style="color:#fff;font-size:14px;font-weight:700;">${el[0].toUpperCase()}</span>
    </div>
    <div style="flex:1;">
      <div style="font-weight:600;text-transform:capitalize;font-size:14px;color:#231F20;">${el}</div>
      <div style="font-size:11px;color:#6b7280;">${EL_DESC[el]}</div>
      ${roleLabel}
    </div>
  </div>`;
}).join('');

const buildPieSvg = (sorted, total) => {
  let cumulativeAngle = 0;
  const pieSlices = sorted.map(([el, score]) => {
    const pct = score / total;
    const startAngle = cumulativeAngle;
    cumulativeAngle += pct * 360;
    const endAngle = cumulativeAngle;
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad = (endAngle - 90) * Math.PI / 180;
    const largeArc = pct > 0.5 ? 1 : 0;
    const x1 = 100 + 80 * Math.cos(startRad);
    const y1 = 100 + 80 * Math.sin(startRad);
    const x2 = 100 + 80 * Math.cos(endRad);
    const y2 = 100 + 80 * Math.sin(endRad);
    if (pct >= 0.999) return `<circle cx="100" cy="100" r="80" fill="${EL_COLORS[el]}" />`;
    if (pct <= 0.001) return '';
    return `<path d="M100,100 L${x1},${y1} A80,80 0 ${largeArc},1 ${x2},${y2} Z" fill="${EL_COLORS[el]}" />`;
  }).join('');

  let cumAngle2 = 0;
  const pieLabels = sorted.map(([el, score]) => {
    const pct = score / total;
    const midAngle = cumAngle2 + (pct * 360) / 2;
    cumAngle2 += pct * 360;
    const midRad = (midAngle - 90) * Math.PI / 180;
    const lx = 100 + 50 * Math.cos(midRad);
    const ly = 100 + 50 * Math.sin(midRad);
    if (pct <= 0.05) return '';
    return `<text x="${lx}" y="${ly}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="11" font-weight="700">${el.charAt(0).toUpperCase() + el.slice(1)}</text>`;
  }).join('');

  return `<svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">${pieSlices}${pieLabels}</svg>`;
};

const buildScenarioHtml = (scenarios) => (scenarios || []).map((si) => {
  const barHtml = (si.stage_elements || []).map(el => `<span style="display:inline-block;width:16px;height:10px;background:${EL_COLORS[el] || '#9ca3af'};border-radius:2px;margin-right:2px;"></span>`).join('');
  return `<div style="margin-bottom:20px;padding-bottom:16px;border-bottom:1px solid #e5e7eb;">
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
      <span style="font-weight:700;text-transform:uppercase;letter-spacing:0.05em;font-size:14px;color:#231F20;">${esc(si.scenario)}</span>
      <span>${barHtml}</span>
    </div>
    <p style="font-size:12px;color:#374151;margin-bottom:10px;line-height:1.6;font-style:italic;">${esc(si.quick_insight)}</p>
    <p style="font-size:13px;color:#1f2937;line-height:1.7;">${esc(si.detailed_insight)}</p>
  </div>`;
}).join('');

const buildArchetypeCardHtml = (archetype, essence_sentence) => `
  <div style="text-align:center;margin-bottom:20px;padding-top:10px;">
    <h1 style="font-size:32px;font-weight:800;text-transform:uppercase;letter-spacing:0.08em;margin:0 0 8px 0;color:#231F20;">${esc(archetype?.name || 'Your Leadership Profile')}</h1>
    <p style="font-size:16px;color:#6b7280;font-style:italic;margin:0;">${esc(archetype?.tagline || essence_sentence)}</p>
  </div>

  <div style="background:#231F20;-webkit-print-color-adjust:exact;print-color-adjust:exact;border-radius:16px;padding:28px 32px;margin-bottom:28px;color:#fff;">
    <div style="display:flex;gap:28px;flex-wrap:wrap;">
      <div style="flex:1;min-width:240px;">
        <h3 style="font-size:15px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin:0 0 12px 0;">Strengths</h3>
        ${(archetype?.strengths || []).map(s => `<p style="font-size:13px;color:#d1d5db;margin:0 0 6px 0;"><span style="color:#FFBD25;">&#9656;</span> ${esc(s)}</p>`).join('')}
      </div>
      <div style="flex:1;min-width:240px;">
        <h3 style="font-size:15px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;margin:0 0 12px 0;">Watchpoints</h3>
        ${(archetype?.watchpoints || []).map(w => `<p style="font-size:13px;color:#d1d5db;margin:0 0 6px 0;"><span style="color:#FF751F;">&#9656;</span> ${esc(w)}</p>`).join('')}
      </div>
    </div>
    ${archetype?.mantra ? `
    <div style="margin-top:20px;padding-top:16px;border-top:1px solid #374151;text-align:center;">
      <p style="font-size:15px;color:#FFD04A;font-style:italic;margin:0;">&ldquo;${esc(archetype.mantra)}&rdquo;</p>
    </div>` : ''}
  </div>`;

const buildPerceptionHtml = (external_perception, closing_style) => {
  if (!external_perception && !closing_style) return '';
  const extHtml = external_perception ? `
    <div style="margin-bottom:${closing_style ? '20px' : '0'};">
      <h3 style="font-size:16px;font-weight:700;margin:0 0 8px 0;">External Perception</h3>
      <p style="font-size:13px;color:#374151;line-height:1.7;">${esc(typeof external_perception === 'object' ? external_perception.expanded || external_perception.condensed : external_perception)}</p>
    </div>` : '';
  const closeHtml = closing_style ? `
    <div>
      <h3 style="font-size:16px;font-weight:700;margin:0 0 8px 0;">Your Closing Style</h3>
      <p style="font-size:13px;color:#374151;line-height:1.7;">${esc(typeof closing_style === 'object' ? closing_style.expanded || closing_style.condensed : closing_style)}</p>
    </div>` : '';
  return `<div style="background:#fff;border-radius:16px;padding:28px;margin-bottom:28px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">${extHtml}${closeHtml}</div>`;
};

const buildReportHtml = (report) => {
  const { profile, archetype, archetype_overview, scenario_insights, growth_path, essence_sentence, external_perception, closing_style } = report;
  const sorted = Object.entries(profile.scores).sort((a, b) => b[1] - a[1]);
  const total = Object.values(profile.scores).reduce((a, b) => a + b, 0) || 1;

  return `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#231F20;max-width:780px;margin:0 auto;padding:0;">
      ${buildArchetypeCardHtml(archetype, essence_sentence)}
      ${buildPerceptionHtml(external_perception, closing_style)}

      <div style="background:#fff;border-radius:16px;padding:28px;margin-bottom:28px;box-shadow:0 1px 3px rgba(0,0,0,0.08);page-break-before:always;">
        <h2 style="font-size:20px;font-weight:700;margin:0 0 4px 0;">Your Elemental Distribution</h2>
        <p style="font-size:13px;color:#6b7280;margin:0 0 16px 0;">How your assessment points are distributed across the four elements</p>
        <div style="display:flex;gap:28px;align-items:center;flex-wrap:wrap;">
          <div style="flex-shrink:0;">${buildPieSvg(sorted, total)}</div>
          <div style="flex:1;min-width:240px;">${buildLegendHtml(sorted, profile)}</div>
        </div>
      </div>

      <div style="background:#fff;border-radius:16px;padding:28px;margin-bottom:28px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h2 style="font-size:20px;font-weight:700;margin:0 0 4px 0;">Your Leadership Pattern</h2>
        <p style="font-size:13px;color:#6b7280;margin:0 0 16px 0;">How your elements combine to shape your executive presence</p>
        <p style="font-size:13px;color:#1f2937;line-height:1.7;white-space:pre-line;">${esc(archetype_overview)}</p>
      </div>

      <div style="background:#fff;border-radius:16px;padding:28px;margin-bottom:28px;box-shadow:0 1px 3px rgba(0,0,0,0.08);page-break-before:always;">
        <h2 style="font-size:20px;font-weight:700;margin:0 0 4px 0;">Scenario Insights</h2>
        <p style="font-size:13px;color:#6b7280;margin:0 0 20px 0;">Your response patterns across five executive challenges</p>
        ${buildScenarioHtml(scenario_insights)}
      </div>

      <div style="background:linear-gradient(135deg,#f0fdf4,#ecfdf5);border-radius:16px;padding:28px;margin-bottom:28px;page-break-before:always;">
        <h2 style="font-size:20px;font-weight:700;margin:0 0 4px 0;">Your Growth Path</h2>
        <p style="font-size:13px;color:#6b7280;margin:0 0 16px 0;">Expanding your leadership repertoire through less-utilized elements</p>
        <p style="font-size:13px;color:#1f2937;line-height:1.7;white-space:pre-line;">${esc(growth_path?.detail || growth_path?.summary || '')}</p>
      </div>

      <div style="text-align:center;padding:20px 0 10px 0;">
        <p style="font-size:15px;font-style:italic;color:#92400e;font-weight:500;">${esc(essence_sentence)}</p>
      </div>
    </div>`;
};

export const generateReportPdf = async (report) => {
  const html2pdf = (await import("html2pdf.js")).default;
  const rawHtml = buildReportHtml(report);
  // Sanitize BEFORE injecting into the DOM to prevent XSS.
  // All string values were already HTML-escaped via esc(), but defence-in-depth matters.
  const safeHtml = DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['svg', 'path', 'circle', 'text'],
    ADD_ATTR: ['xmlns', 'viewBox', 'cx', 'cy', 'r', 'd', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
               'fill', 'stroke', 'stroke-width', 'width', 'height',
               'text-anchor', 'dominant-baseline', 'font-size', 'font-weight']
  });
  const container = document.createElement("div");
  container.innerHTML = safeHtml;
  document.body.appendChild(container);

  const opt = {
    margin: [12, 10, 12, 10],
    filename: `Brain360_Report_${report.archetype?.name?.replace(/\s+/g, '_') || 'Leadership'}.pdf`,
    image: { type: "jpeg", quality: 0.95 },
    html2canvas: { scale: 2, useCORS: true, logging: false },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ["css"] }
  };

  try {
    await html2pdf().set(opt).from(container).save();
  } finally {
    document.body.removeChild(container);
  }
};
